import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { publicRouter } from '@/router/publicRouter';
import { PublicErrorBoundary } from '@/components/public/PublicErrorBoundary';
import { applyPublicPlayerTheme, scheduleOnIdle } from '@/publicBootstrap';
import { startPublicTrace } from '@/public/publicTrace';
import { loadPublicQuizStrings } from '@/utils/publicQuizStrings';
import { resolveVisitorLanguage } from '@/utils/visitorLanguage';
import { resumeConsentReports } from '@/utils/analytics/trackEvent';
import { isPreviewLinkHost } from '@/config/publicEntryHosts';
import { PUBLIC_APEX, PUBLIC_PREVIEW_SUBDOMAIN } from '@/config/public-host';
import '@/styles/global.css';

/**
 * WEB-1988 — ВТОРОЙ вход сборки: страница покупателя воронки.
 *
 * Кабинетный вход (`main.tsx`) поднимает Ant Design, Redux-стор, проверку
 * сессии и роутер со всеми разделами. Покупателю воронки не нужно ничего из
 * этого — а получал он всё (живой замер 12.09.2026: ≈856 КБ JS по проводу,
 * включая чанк кабинета `requiredGate` и запрос `/auth/me` → 401).
 *
 * Что здесь есть: React, роутер публичных страниц, граница падения без antd,
 * светлая тема полотна. Чего нет: antd, стора кабинета, страниц кабинета —
 * и это не обещание, а сторож: `src/build-guards/publicEntryGuard.ts` разбирает
 * настоящий граф `vite build` и падает на любом таком импорте, плюс держит
 * бюджет 150 КБ gzip до первого экрана.
 */

applyPublicPlayerTheme();

// WEB-1991 (возврат приёмки 13.09) — трейс начинается ЗДЕСЬ, СИНХРОННО, до
// первого рендера: `RouterProvider` ниже тут же уходит за `/public/resolve`, а
// ленивый Sentry к этому моменту ещё не загружен, и первый — главный — запрос
// оставался без `sentry-trace`. Предзагрузчик не тянет SDK (вес первого экрана
// не растёт, см. `build-guards/publicEntryGuard.ts`): он заводит трейс сам и
// оставляет мета-тег, из которого SDK продолжит ЭТОТ ЖЕ трейс.
startPublicTrace();

// Наблюдаемость — после первого экрана, отдельным ленивым чанком:
// WEB-2178 — ЗДЕСЬ БОЛЬШЕ НЕТ ВЫЗОВА `schedulePublicObservability()`. Раньше
// она грузила КАБИНЕТНУЮ настройку (`@/config/sentry`, `initSentry()`) —
// ту же, что у кабинета, с ВКЛЮЧЁННОЙ записью экрана (session-replay). На
// одном и том же экземпляре SDK это была ВТОРАЯ настройка поверх публичной
// (`initPublicTracing()` ниже), и порядок догрузки чанков решал, чья победит:
// живой прогон 16.09 поймал `sentryReplaySession` в sessionStorage на боевой
// воронке — запись экрана реально работала там, где в DOM живут почта,
// телефон и поля карты. Публичный контур не имеет права звать `initSentry()`
// вовсе — это проверяется структурно, а не обещанием: `publicEntryGuard.ts`
// (правило `cabinet-sentry`) падает на любом пути от входа до
// `src/config/sentry.ts`, статическом или динамическом. Отчёт об ошибках и
// трассировка (WEB-1991) от этого не страдают: `initPublicTracing()` ниже —
// САМОСТОЯТЕЛЬНАЯ настройка, которая сама ловит и ошибки, и трейсы, без
// второго Sentry.init.
// WEB-1991 + WEB-2015 (П1+П2): трассировка публичного контура — динамическим
// импортом (модуль и ленивый @sentry/react за ним не попадают в первый чанк),
// И ТЕМ ЖЕ ПРОСТОЕМ БРАУЗЕРА, что и Sentry выше — раньше `import()` уходил
// СРАЗУ при исполнении скрипта и на медленной сети боролся за пропускную
// способность с первым экраном (живой замер 16.09 поймал это ДВУМЯ файлами:
// `sdk-*.js` — `@sentry/react`, и `index-*.js` — общий с кабинетом
// `@sentry/core`, названный так Rollup'ом за неимением facadeModuleId).
// Момент загрузки SDK на сам трейс не влияет: заголовок и мета-тег первого
// запроса уже поставлены синхронно выше (`startPublicTrace()`) — SDK лишь
// ПРОДОЛЖИТ этот же трейс, когда догрузится, независимо от того, когда это
// случится. Без DSN — no-op (см. `initPublicTracing`).
//
// Свежий взгляд пачки 49 (06.10.2026): на хосте ссылки предпросмотра
// (`preview.<апекс>/<токен>`) трассировка и Sentry НЕ поднимаются вовсе. Путь
// окна там — сам токен ссылки, предъявительский ключ к неопубликованной
// воронке, а SDK называет транзакцию загрузки путём окна и кладёт адрес окна
// в каждое событие. Заодно это запрос на сторону, которого со страницы
// получателя быть не должно. Правило хоста — то же, что у роутера
// (`router/publicRouter.tsx`), оба модуля уже в первом экране (условие стоит
// 12 байт веса первого экрана, замер 06.10). Вторая линия — маска длинного
// сегмента пути в `utils/scrubPii.ts`; сторож —
// `public-main.previewHost.web2115.test.ts`.
scheduleOnIdle(() => {
  if (
    !isPreviewLinkHost(
      window.location.hostname,
      PUBLIC_APEX,
      PUBLIC_PREVIEW_SUBDOMAIN,
    )
  ) {
    void import('@/config/sentryPublicTracing')
      .then((m) => m.initPublicTracing())
      .catch(() => {
        // Чанк не приехал (404 после смены хешей сборки, офлайн-момент) —
        // покупатель без трассировки. Это не повод падать.
      });
  }
  // WEB-2672: отказ от cookie, не доставленный на прошлой загрузке (не было
  // сети, вкладку закрыли), лежит отметкой на устройстве — досылаем его тем
  // же простоем браузера (общий вызов простоя и ленивый импорт в соседстве с
  // таким же в trackEvent.ts — ради веса первого экрана). Заодно модуль
  // доставки остаётся загруженным: отказ, нажатый уже без сети, не зависит от
  // запроса за чанком.
  resumeConsentReports();
});
// сюда: initPublicVitals — WEB-1992 (зовётся на страницах-шагах, см. PublicQuiz/PublicPaywall)
// (трассировка и web-vitals публичной воронки приедут своими карточками;
// подключать их надо здесь же, ленивым вызовом рядом с телеметрией выше,
// чтобы бюджет первого экрана остался нетронутым).

// WEB-2057: надписи воронки едут пакетом по языку. Пакет ЯЗЫКА БРАУЗЕРА
// греется сразу, параллельно резолву: им говорят системные экраны, которые
// показываются ДО загрузки воронки (аварийный экран границы падения,
// «страница недоступна»). Греется без ожидания — экран воронки ждёт свой пакет
// сам (PublicQuiz/PublicPaywall), а этот запрос только сокращает окно, в
// котором системный текст был бы английским.
void loadPublicQuizStrings(resolveVisitorLanguage(null)).catch(() => {
  // Не приехал — системные экраны скажут по-английски. Это не повод падать.
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <PublicErrorBoundary>
      <RouterProvider router={publicRouter} />
    </PublicErrorBoundary>
  </StrictMode>,
);
